// Recipe Module (docs/RECIPE_MODULE.md) — the shared recipe library list, combining Phase 1
// (percentage-based — sauces/general) and Phase 2 (baker's-percentage dough) recipes into one
// list, tagged by type since each has its own editor/run flow and math model.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import recipeService from '../services/recipeService';
import ConfirmModal from '../components/ConfirmModal';
import RunHistoryModal from '../components/RunHistoryModal';
import { usePermissions } from '../context/PermissionContext';

const fmtCost = (v, unit) => (v == null ? '—' : `$${Number(v).toFixed(2)}/${unit}`);

export default function RecipeLibraryPage() {
  const navigate = useNavigate();
  const { role } = usePermissions();
  const canManage = role === 'admin' || role === 'superadmin' || role === 'qa';

  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [historyTarget, setHistoryTarget] = useState(null);

  const fetchRecipes = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [percentage, dough] = await Promise.all([
        recipeService.listPercentageRecipes(),
        recipeService.listDoughRecipes(),
      ]);
      const combined = [
        ...(Array.isArray(percentage) ? percentage : []).map(r => ({
          id: r.id, type: 'percentage', name: r.name, category: r.category, status: r.status,
          basisLabel: r.yieldBasisLabel || (r.baseIngredientName ? `Cooked ${r.baseIngredientName}` : '—'),
          costPerKg: r.costPerKg, costUnit: 'kg',
        })),
        ...(Array.isArray(dough) ? dough : []).map(r => ({
          id: r.id, type: 'dough', name: r.name, category: r.category, status: r.status,
          basisLabel: `${r.hydrationPercent}% hydration${r.preFermentType !== 'none' ? ` · ${r.preFermentType}` : ''}`,
          costPerKg: r.costPerKgDough, costUnit: 'kg',
        })),
      ].sort((a, b) => a.name.localeCompare(b.name));
      setRecipes(combined);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load recipes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRecipes(); }, [fetchRecipes]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      if (deleteTarget.type === 'dough') await recipeService.deleteDoughRecipe(deleteTarget.id);
      else await recipeService.deletePercentageRecipe(deleteTarget.id);
      setDeleteTarget(null);
      fetchRecipes();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete recipe.');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const path = (r, action) => `/recipes/${r.type}/${r.id}/${action}`;

  return (
    <div className="container mt-4 pb-5">
      <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-start align-items-md-center mb-4 p-4 rounded gap-3"
        style={{ background: 'linear-gradient(135deg, #198754 0%, #157347 100%)' }}>
        <div>
          <h4 className="mb-0 text-white">📖 Recipe Library</h4>
          <small className="text-white" style={{ opacity: 0.85 }}>Enter the real yield/batch, get the scaled ingredient list — percentage recipes and baker's-percentage dough.</small>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <button className="btn btn-outline-light btn-sm fw-semibold" onClick={() => navigate('/recipes/knowledge-base')}>
            📚 Knowledge Base
          </button>
          {canManage && (
            <>
              <button className="btn btn-light btn-sm fw-semibold" onClick={() => navigate('/recipes/percentage/new')}>
                + New Recipe
              </button>
              <button className="btn btn-light btn-sm fw-semibold" onClick={() => navigate('/recipes/dough/new')}>
                + New Dough Recipe
              </button>
            </>
          )}
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      {loading ? (
        <div className="text-center text-muted py-5">
          <div className="spinner-border spinner-border-sm me-2" />Loading…
        </div>
      ) : recipes.length === 0 ? (
        <div className="text-center text-muted py-5">
          <p className="mb-2">No recipes yet.</p>
          {canManage && (
            <button className="btn btn-outline-primary btn-sm" onClick={() => navigate('/recipes/percentage/new')}>
              Create your first recipe
            </button>
          )}
        </div>
      ) : (
        <div className="card" style={{ border: '1px solid #e9ecef', borderTopWidth: 3, borderTopStyle: 'solid', borderTopColor: '#198754' }}>
          <div className="table-responsive">
          <table className="table table-hover table-sm align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Category</th>
                <th>Basis</th>
                <th>Status</th>
                <th>Cost</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {recipes.map(r => (
                <tr key={`${r.type}-${r.id}`}>
                  <td className="fw-semibold">{r.name}</td>
                  <td>
                    <span className="badge bg-light text-dark border" style={{ fontSize: 11 }}>
                      {r.type === 'dough' ? '🍞 Dough' : '🧪 Percentage'}
                    </span>
                  </td>
                  <td>{r.category || <span className="text-muted">—</span>}</td>
                  <td>{r.basisLabel}</td>
                  <td>
                    <span className={`badge rounded-pill ${r.status === 'approved' ? 'bg-success' : 'bg-secondary'}`}>
                      {r.status === 'approved' ? '✓ Approved' : 'Draft'}
                    </span>
                  </td>
                  <td className="fw-semibold" style={{ color: '#198754' }}>{fmtCost(r.costPerKg, r.costUnit)}</td>
                  <td className="text-end">
                    <div className="d-flex gap-1 justify-content-end">
                      <button className="btn btn-outline-success btn-sm" onClick={() => navigate(path(r, 'run'))}>
                        ▶ Run
                      </button>
                      <button className="btn btn-outline-dark btn-sm" onClick={() => setHistoryTarget(r)}>
                        🕐 History
                      </button>
                      {canManage && (
                        <>
                          <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(path(r, 'edit'))}>
                            Edit
                          </button>
                          <button className="btn btn-outline-danger btn-sm" onClick={() => setDeleteTarget(r)}>
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <ConfirmModal
        show={!!deleteTarget}
        title="Delete recipe?"
        message={`This permanently deletes "${deleteTarget?.name}" and all its lines and steps. Past execution logs are kept.`}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {historyTarget && (
        <RunHistoryModal recipe={historyTarget} onClose={() => setHistoryTarget(null)} />
      )}
    </div>
  );
}
